import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FolderKanban, Plus, ChevronDown, ChevronRight, RefreshCw,
  Calendar, CheckCircle2, Circle, Clock, MoreHorizontal,
  RotateCcw, X, Pencil, Trash2, Layers, ListTodo,
} from 'lucide-react'
import { api } from '../lib/api'

const C = {
  bg: 'var(--color-bg)', bgRaised: 'var(--color-bg-card)', bgCard: '#1A2212',
  border: 'var(--color-border)', amber: 'var(--color-primary)', green: '#2D6A2F',
  text: 'var(--color-text)', textMuted: 'var(--color-text-muted)', textFaint: 'var(--color-text-faint)',
  red: '#F87171', blue: '#60A5FA', teal: '#2DD4BF', purple: '#A78BFA',
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: 8, padding: '8px 12px',
  color: C.text, fontSize: 13, outline: 'none',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 500, color: C.textMuted, marginBottom: 5,
}
const btnPrimary: React.CSSProperties = {
  background: C.amber, color: '#1A1200', border: 'none', borderRadius: 8,
  padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
}
const btnSecondary: React.CSSProperties = {
  background: 'transparent', color: C.textMuted,
  border: `1px solid ${C.border}`, borderRadius: 8,
  padding: '8px 14px', fontSize: 13, cursor: 'pointer',
}
const btnGhost: React.CSSProperties = {
  background: 'transparent', border: 'none', color: C.textMuted,
  cursor: 'pointer', padding: '4px 8px', borderRadius: 6, fontSize: 13,
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface RecurrenceRule {
  frequency: string   // once | annually | monthly | weekly | custom
  month?: number
  day_of_month?: number
  week_day?: number
}

interface Project {
  id: string
  name: string
  description: string
  project_type: 'one_time' | 'recurring'
  status: 'active' | 'on_hold' | 'completed' | 'archived'
  color_hex: string
  start_date?: string
  due_date?: string
  completed_at?: string
  recurrence_rule?: RecurrenceRule
  auto_advance_days: number
  created_at: string
  instance_count: number
  open_task_count: number
}

interface ProjectInstance {
  id: string
  project_id: string
  project_name?: string
  project_color?: string
  name: string
  status: 'active' | 'on_hold' | 'completed' | 'archived'
  year?: number
  season?: string
  start_date?: string
  due_date?: string
  completed_at?: string
  task_count: number
  task_completed: number
  task_overdue: number
  created_at: string
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const SEASON_COLORS: Record<string, string> = {
  spring: '#4ADE80', summer: '#FACC15', fall: '#FB923C', winter: '#93C5FD',
}

function progressPct(inst: ProjectInstance): number {
  if (inst.task_count === 0) return 0
  return Math.round((inst.task_completed / inst.task_count) * 100)
}

function statusBadge(status: string) {
  const map: Record<string, { label: string; color: string }> = {
    active:    { label: 'Active',    color: C.green },
    on_hold:   { label: 'On Hold',  color: C.amber },
    completed: { label: 'Done',     color: C.teal },
    archived:  { label: 'Archived', color: C.textFaint },
  }
  const s = map[status] ?? { label: status, color: C.textMuted }
  return (
    <span style={{
      background: s.color + '22', color: s.color, border: `1px solid ${s.color}44`,
      borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600,
    }}>{s.label}</span>
  )
}

function recurrenceLabel(r?: RecurrenceRule): string {
  if (!r || r.frequency === 'once') return 'One-time'
  const months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  if (r.frequency === 'annually' && r.month) return `Annually (${months[r.month]})`
  if (r.frequency === 'annually') return 'Annual'
  if (r.frequency === 'monthly') return 'Monthly'
  if (r.frequency === 'weekly') return 'Weekly'
  return r.frequency
}

function fmtDate(d?: string) {
  if (!d) return '—'
  return new Date(d + (d.length === 10 ? 'T00:00:00' : '')).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric'
  })
}

// ── Modal: Create / Edit Project ──────────────────────────────────────────────

const PALETTE = [
  '#F5A623', '#2D6A2F', '#60A5FA', '#F87171',
  '#A78BFA', '#2DD4BF', '#FB923C', '#4ADE80',
]

interface ProjectModalProps {
  onClose: () => void
  initial?: Project
}

function ProjectModal({ onClose, initial }: ProjectModalProps) {
  const qc = useQueryClient()
  const [name, setName] = useState(initial?.name ?? '')
  const [desc, setDesc] = useState(initial?.description ?? '')
  const [type, setType] = useState<'one_time' | 'recurring'>(initial?.project_type ?? 'one_time')
  const [color, setColor] = useState(initial?.color_hex ?? C.amber)
  const [startDate, setStartDate] = useState(initial?.start_date ?? '')
  const [dueDate, setDueDate] = useState(initial?.due_date ?? '')
  const [rrFreq, setRrFreq] = useState(initial?.recurrence_rule?.frequency ?? 'annually')
  const [rrMonth, setRrMonth] = useState(initial?.recurrence_rule?.month ?? 3)
  const [autoAdvance, setAutoAdvance] = useState(initial?.auto_advance_days ?? 30)
  const [err, setErr] = useState('')

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      initial
        ? api.put(`/projects/${initial.id}`, body)
        : api.post('/projects', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      onClose()
    },
    onError: (e: Error) => setErr(e.message),
  })

  function submit() {
    if (!name.trim()) { setErr('Name is required'); return }
    const body: Record<string, unknown> = {
      name: name.trim(), description: desc,
      project_type: type,
      color_hex: color,
      start_date: startDate || null,
      due_date: dueDate || null,
      auto_advance_days: autoAdvance,
    }
    if (type === 'recurring') {
      body.recurrence_rule = { frequency: rrFreq, month: rrFreq === 'annually' ? rrMonth : undefined }
    }
    mutation.mutate(body)
  }

  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  }
  const modal: React.CSSProperties = {
    background: C.bgCard, border: `1px solid ${C.border}`,
    borderRadius: 14, padding: 28, width: 480, maxWidth: '95vw',
  }

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>
            {initial ? 'Edit Project' : 'New Project'}
          </span>
          <button style={btnGhost} onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Name *</label>
            <input style={inputStyle} value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Spring Planting" />
          </div>

          <div>
            <label style={labelStyle}>Description</label>
            <textarea style={{ ...inputStyle, height: 72, resize: 'vertical' }}
              value={desc} onChange={e => setDesc(e.target.value)} />
          </div>

          <div>
            <label style={labelStyle}>Type</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['one_time', 'recurring'] as const).map(t => (
                <button key={t} onClick={() => setType(t)} style={{
                  flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13,
                  cursor: 'pointer', fontWeight: 600,
                  border: `1px solid ${type === t ? C.amber : C.border}`,
                  background: type === t ? 'color-mix(in srgb, var(--color-primary) 13%, transparent)' : 'transparent',
                  color: type === t ? C.amber : C.textMuted,
                }}>
                  {t === 'one_time' ? 'One-time' : 'Recurring'}
                </button>
              ))}
            </div>
          </div>

          {type === 'recurring' && (
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Frequency</label>
                <select style={{ ...inputStyle }} value={rrFreq} onChange={e => setRrFreq(e.target.value)}>
                  <option value="annually">Annually</option>
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              {rrFreq === 'annually' && (
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Month</label>
                  <select style={{ ...inputStyle }} value={rrMonth} onChange={e => setRrMonth(Number(e.target.value))}>
                    {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                      <option key={m} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {type === 'recurring' && (
            <div>
              <label style={labelStyle}>Auto-advance (days before due date to create next instance)</label>
              <input style={inputStyle} type="number" min={0} value={autoAdvance}
                onChange={e => setAutoAdvance(Number(e.target.value))} />
            </div>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Start date</label>
              <input style={inputStyle} type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Due date</label>
              <input style={inputStyle} type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Color</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {PALETTE.map(c => (
                <button key={c} onClick={() => setColor(c)} style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: c, border: color === c ? `2px solid ${C.text}` : '2px solid transparent',
                  cursor: 'pointer',
                }} />
              ))}
            </div>
          </div>

          {err && <div style={{ color: C.red, fontSize: 12 }}>{err}</div>}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button style={btnSecondary} onClick={onClose}>Cancel</button>
            <button style={btnPrimary} onClick={submit} disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : initial ? 'Save changes' : 'Create project'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Modal: Create Instance ────────────────────────────────────────────────────

interface InstanceModalProps {
  project: Project
  onClose: () => void
}

function InstanceModal({ project, onClose }: InstanceModalProps) {
  const qc = useQueryClient()
  const year = new Date().getFullYear()
  const [name, setName] = useState(`${project.name} ${year}`)
  const [instYear, setInstYear] = useState<number>(year)
  const [season, setSeason] = useState('')
  const [startDate, setStartDate] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [err, setErr] = useState('')

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post(`/projects/${project.id}/instances`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects', project.id, 'instances'] })
      onClose()
    },
    onError: (e: Error) => setErr(e.message),
  })

  function submit() {
    if (!name.trim()) { setErr('Name is required'); return }
    mutation.mutate({
      name: name.trim(),
      year: instYear || null,
      season: season || null,
      start_date: startDate || null,
      due_date: dueDate || null,
    })
  }

  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  }
  const modal: React.CSSProperties = {
    background: C.bgCard, border: `1px solid ${C.border}`,
    borderRadius: 14, padding: 28, width: 440, maxWidth: '95vw',
  }

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>New Instance</span>
          <button style={btnGhost} onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Name *</label>
            <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} />
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Year</label>
              <input style={inputStyle} type="number" value={instYear}
                onChange={e => setInstYear(Number(e.target.value))} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Season</label>
              <select style={{ ...inputStyle }} value={season} onChange={e => setSeason(e.target.value)}>
                <option value="">— none —</option>
                <option value="spring">Spring</option>
                <option value="summer">Summer</option>
                <option value="fall">Fall</option>
                <option value="winter">Winter</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Start date</label>
              <input style={inputStyle} type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Due date</label>
              <input style={inputStyle} type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
          </div>

          {err && <div style={{ color: C.red, fontSize: 12 }}>{err}</div>}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button style={btnSecondary} onClick={onClose}>Cancel</button>
            <button style={btnPrimary} onClick={submit} disabled={mutation.isPending}>
              {mutation.isPending ? 'Creating…' : 'Create instance'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Instance Card ─────────────────────────────────────────────────────────────

interface InstanceCardProps {
  inst: ProjectInstance
  projectId: string
  color: string
}

function InstanceCard({ inst, projectId, color }: InstanceCardProps) {
  const qc = useQueryClient()
  const pct = progressPct(inst)

  const updateStatus = useMutation({
    mutationFn: (status: string) =>
      api.put(`/projects/${projectId}/instances/${inst.id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects', projectId, 'instances'] }),
  })

  const nextStatus = inst.status === 'active' ? 'completed'
    : inst.status === 'completed' ? 'active' : 'active'

  return (
    <div style={{
      background: C.bgRaised, border: `1px solid ${C.border}`,
      borderRadius: 10, padding: '14px 16px',
      borderLeft: `3px solid ${color}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <button style={{ ...btnGhost, padding: 2 }}
          onClick={() => updateStatus.mutate(nextStatus)}
          title={`Mark as ${nextStatus}`}>
          {inst.status === 'completed'
            ? <CheckCircle2 size={16} color={C.teal} />
            : <Circle size={16} color={C.textFaint} />}
        </button>
        <span style={{
          color: C.text, fontWeight: 600, fontSize: 14,
          textDecoration: inst.status === 'completed' ? 'line-through' : 'none',
        }}>{inst.name}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          {inst.season && (
            <span style={{
              background: (SEASON_COLORS[inst.season] ?? C.amber) + '22',
              color: SEASON_COLORS[inst.season] ?? C.amber,
              borderRadius: 5, padding: '2px 7px', fontSize: 11, fontWeight: 600,
            }}>{inst.season.charAt(0).toUpperCase() + inst.season.slice(1)}</span>
          )}
          {inst.year && (
            <span style={{ color: C.textFaint, fontSize: 12 }}>{inst.year}</span>
          )}
          {statusBadge(inst.status)}
        </div>
      </div>

      {/* Progress bar */}
      {inst.task_count > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 11, color: C.textMuted }}>
              {inst.task_completed}/{inst.task_count} tasks
            </span>
            <span style={{ fontSize: 11, color: C.textMuted }}>{pct}%</span>
          </div>
          <div style={{ height: 4, background: C.border, borderRadius: 2 }}>
            <div style={{
              height: '100%', borderRadius: 2,
              width: `${pct}%`,
              background: pct === 100 ? C.teal : color,
              transition: 'width 0.3s',
            }} />
          </div>
          {inst.task_overdue > 0 && (
            <span style={{ fontSize: 11, color: C.red, marginTop: 4, display: 'block' }}>
              {inst.task_overdue} task{inst.task_overdue !== 1 ? 's' : ''} overdue
            </span>
          )}
        </div>
      )}

      {inst.task_count === 0 && (
        <div style={{ fontSize: 12, color: C.textFaint, marginBottom: 8 }}>
          No tasks yet
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: C.textFaint }}>
        {inst.start_date && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Calendar size={11} /> {fmtDate(inst.start_date)}
          </span>
        )}
        {inst.due_date && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Clock size={11} /> Due {fmtDate(inst.due_date)}
          </span>
        )}
        {inst.completed_at && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.teal }}>
            <CheckCircle2 size={11} /> Completed {fmtDate(inst.completed_at.slice(0, 10))}
          </span>
        )}
      </div>
    </div>
  )
}

// ── Project Row ───────────────────────────────────────────────────────────────

interface ProjectRowProps {
  project: Project
  onEdit: (p: Project) => void
  onDelete: (p: Project) => void
  onNewInstance: (p: Project) => void
  onAdvance: (p: Project) => void
}

function ProjectRow({ project, onEdit, onDelete, onNewInstance, onAdvance }: ProjectRowProps) {
  const [expanded, setExpanded] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  const { data: instances } = useQuery<ProjectInstance[]>({
    queryKey: ['projects', project.id, 'instances'],
    queryFn: () => api.get(`/projects/${project.id}/instances`),
    enabled: expanded,
  })

  return (
    <div style={{
      background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
        cursor: 'pointer', borderBottom: expanded ? `1px solid ${C.border}` : 'none',
      }} onClick={() => setExpanded(e => !e)}>
        {/* Color dot */}
        <div style={{
          width: 12, height: 12, borderRadius: '50%',
          background: project.color_hex, flexShrink: 0,
        }} />

        {/* Expand chevron */}
        {expanded
          ? <ChevronDown size={15} color={C.textFaint} />
          : <ChevronRight size={15} color={C.textFaint} />}

        {/* Name + badges */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>{project.name}</span>
            {statusBadge(project.status)}
            <span style={{
              background: C.border, color: C.textMuted,
              borderRadius: 5, padding: '2px 7px', fontSize: 11,
            }}>
              {project.project_type === 'recurring'
                ? <><RotateCcw size={10} style={{ marginRight: 4 }} />{recurrenceLabel(project.recurrence_rule)}</>
                : 'One-time'}
            </span>
          </div>
          {project.description && (
            <div style={{ color: C.textFaint, fontSize: 12, marginTop: 2,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {project.description}
            </div>
          )}
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 16, flexShrink: 0 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>{project.instance_count}</div>
            <div style={{ color: C.textFaint, fontSize: 10 }}>instances</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: project.open_task_count > 0 ? C.amber : C.textFaint, fontWeight: 700, fontSize: 16 }}>
              {project.open_task_count}
            </div>
            <div style={{ color: C.textFaint, fontSize: 10 }}>open tasks</div>
          </div>
        </div>

        {/* Menu */}
        <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
          <button style={btnGhost} onClick={() => setMenuOpen(m => !m)}>
            <MoreHorizontal size={16} />
          </button>
          {menuOpen && (
            <div style={{
              position: 'absolute', right: 0, top: 28, zIndex: 200,
              background: C.bgCard, border: `1px solid ${C.border}`,
              borderRadius: 10, padding: '6px 0', minWidth: 170,
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            }}>
              {[
                { label: 'Edit project', icon: <Pencil size={13} />, action: () => { onEdit(project); setMenuOpen(false) } },
                { label: 'New instance', icon: <Plus size={13} />, action: () => { onNewInstance(project); setMenuOpen(false) } },
                ...(project.project_type === 'recurring' ? [{
                  label: 'Advance recurring', icon: <RotateCcw size={13} />, action: () => { onAdvance(project); setMenuOpen(false) }
                }] : []),
                { label: 'Delete project', icon: <Trash2 size={13} />, action: () => { onDelete(project); setMenuOpen(false) }, danger: true },
              ].map(item => (
                <button key={item.label} style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  background: 'transparent', border: 'none', padding: '8px 14px',
                  color: (item as { danger?: boolean }).danger ? C.red : C.text,
                  cursor: 'pointer', fontSize: 13, textAlign: 'left',
                }} onClick={item.action}>
                  {item.icon} {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Instances */}
      {expanded && (
        <div style={{ padding: '12px 16px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {!instances && (
            <div style={{ color: C.textFaint, fontSize: 13 }}>Loading…</div>
          )}
          {instances && instances.length === 0 && (
            <div style={{ color: C.textFaint, fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
              No instances yet.{' '}
              <button style={{ ...btnGhost, color: C.amber, padding: 0 }}
                onClick={() => onNewInstance(project)}>
                Create one →
              </button>
            </div>
          )}
          {instances && instances.map(inst => (
            <InstanceCard key={inst.id} inst={inst}
              projectId={project.id} color={project.color_hex} />
          ))}
          <button style={{
            ...btnSecondary, display: 'flex', alignItems: 'center', gap: 6,
            justifyContent: 'center', marginTop: 4,
          }} onClick={() => onNewInstance(project)}>
            <Plus size={13} /> New instance
          </button>
        </div>
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ProjectsPage() {
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<Project | null>(null)
  const [newInstanceFor, setNewInstanceFor] = useState<Project | null>(null)
  const [filter, setFilter] = useState<'all' | 'active' | 'completed' | 'recurring'>('all')

  const { data: projects, isLoading, error } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: () => api.get('/projects'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/projects/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  })

  const advanceMutation = useMutation({
    mutationFn: (id: string) => api.post(`/projects/${id}/advance`, {}),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      qc.invalidateQueries({ queryKey: ['projects', id, 'instances'] })
    },
  })

  function handleDelete(p: Project) {
    if (!window.confirm(`Delete project "${p.name}"? This cannot be undone.`)) return
    deleteMutation.mutate(p.id)
  }

  function handleAdvance(p: Project) {
    advanceMutation.mutate(p.id)
  }

  const filtered = (projects ?? []).filter(p => {
    if (filter === 'active') return p.status === 'active'
    if (filter === 'completed') return p.status === 'completed'
    if (filter === 'recurring') return p.project_type === 'recurring'
    return true
  })

  const stats = {
    total: (projects ?? []).length,
    active: (projects ?? []).filter(p => p.status === 'active').length,
    recurring: (projects ?? []).filter(p => p.project_type === 'recurring').length,
    openTasks: (projects ?? []).reduce((s, p) => s + p.open_task_count, 0),
  }

  return (
    <main id="main-content" style={{ background: C.bg, minHeight: '100dvh', padding: '28px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <FolderKanban size={22} color={C.amber} />
            <h1 style={{ margin: 0, color: C.text, fontSize: 22, fontWeight: 800 }}>Projects</h1>
          </div>
          <p style={{ margin: 0, color: C.textMuted, fontSize: 13 }}>
            Master projects, recurring annual plans, and task instances
          </p>
        </div>
        <button style={{ ...btnPrimary, display: 'flex', alignItems: 'center', gap: 6 }}
          onClick={() => setShowCreate(true)}>
          <Plus size={14} /> New Project
        </button>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total projects', value: stats.total, icon: <Layers size={16} />, color: C.amber },
          { label: 'Active', value: stats.active, icon: <Circle size={16} />, color: C.green },
          { label: 'Recurring', value: stats.recurring, icon: <RotateCcw size={16} />, color: C.purple },
          { label: 'Open tasks', value: stats.openTasks, icon: <ListTodo size={16} />, color: C.blue },
        ].map(s => (
          <div key={s.label} style={{
            background: C.bgCard, border: `1px solid ${C.border}`,
            borderRadius: 12, padding: '16px 18px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, color: s.color }}>
              {s.icon}
              <span style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {s.label}
              </span>
            </div>
            <div style={{ color: C.text, fontSize: 26, fontWeight: 800 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {([
          { key: 'all', label: 'All' },
          { key: 'active', label: 'Active' },
          { key: 'completed', label: 'Completed' },
          { key: 'recurring', label: 'Recurring' },
        ] as const).map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '6px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600,
            border: `1px solid ${filter === f.key ? C.amber : C.border}`,
            background: filter === f.key ? 'color-mix(in srgb, var(--color-primary) 13%, transparent)' : 'transparent',
            color: filter === f.key ? C.amber : C.textMuted,
          }}>{f.label}</button>
        ))}
        {advanceMutation.isPending && (
          <span style={{ color: C.textFaint, fontSize: 12, alignSelf: 'center', marginLeft: 8 }}>
            <RefreshCw size={12} style={{ display: 'inline', marginRight: 4 }} />
            Advancing…
          </span>
        )}
      </div>

      {/* Content */}
      {isLoading && (
        <div style={{ color: C.textFaint, fontSize: 14, textAlign: 'center', padding: 48 }}>
          Loading projects…
        </div>
      )}

      {error && (
        <div style={{ color: C.red, fontSize: 14, textAlign: 'center', padding: 48 }}>
          Failed to load projects
        </div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '56px 24px',
          background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 14,
        }}>
          <FolderKanban size={36} color={C.textFaint} style={{ marginBottom: 12 }} />
          <div style={{ color: C.text, fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
            {filter === 'all' ? 'No projects yet' : `No ${filter} projects`}
          </div>
          <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>
            {filter === 'all'
              ? 'Create your first project to start planning farm work'
              : 'Try a different filter'}
          </div>
          {filter === 'all' && (
            <button style={btnPrimary} onClick={() => setShowCreate(true)}>
              <Plus size={14} style={{ display: 'inline', marginRight: 4 }} />
              Create project
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filtered.map(p => (
          <ProjectRow
            key={p.id}
            project={p}
            onEdit={setEditing}
            onDelete={handleDelete}
            onNewInstance={setNewInstanceFor}
            onAdvance={handleAdvance}
          />
        ))}
      </div>

      {/* Modals */}
      {showCreate && <ProjectModal onClose={() => setShowCreate(false)} />}
      {editing && <ProjectModal onClose={() => setEditing(null)} initial={editing} />}
      {newInstanceFor && (
        <InstanceModal project={newInstanceFor} onClose={() => setNewInstanceFor(null)} />
      )}
    </main>
  )
}
